import { z } from "zod";
import {
  normalizeContentBlockFromApi,
  normalizeTextMarks,
} from "../../utils/articleParagraphUtils";

const isoDateTimeSchema = z.union([
  z.string(),
  z.number().transform((value) => new Date(value).toISOString()),
  z.array(z.number()).transform((parts) => {
    const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = parts;
    const millis = Math.floor(Number(nano) / 1_000_000);
    return new Date(year, month - 1, day, hour, minute, second, millis).toISOString();
  }),
]);

export const imageScopeSchema = z.enum(["ARTICLES", "NEWSLETTER"]);

export const articleTextMarksSchema = z.preprocess(
  (marks) => normalizeTextMarks(marks) ?? undefined,
  z
    .object({
      color: z.string().optional(),
      weight: z.enum(["normal", "bold"]).optional(),
      style: z.enum(["normal", "italic"]).optional(),
      fontSize: z.coerce.number().int().positive().optional(),
    })
    .optional()
);

export const articleTextRunSchema = z.object({
  text: z.string(),
  marks: articleTextMarksSchema.nullable().optional(),
});

export const articleParagraphBlockSchema = z.object({
  type: z.literal("paragraph"),
  align: z.enum(["left", "center", "justify"]).optional(),
  runs: z.array(articleTextRunSchema).min(1),
});

export const articleImageBlockSchema = z.object({
  type: z.literal("image"),
  objectKey: z.string().min(1),
  publicUrl: z.string().min(1),
  alt: z.string().nullable().optional(),
});

export const articleContentBlockSchema = z.discriminatedUnion("type", [
  articleParagraphBlockSchema,
  articleImageBlockSchema,
]);

export const articleContentSchema = z.object({
  version: z.number().int().positive().default(1),
  blocks: z.preprocess(
    (blocks) => (Array.isArray(blocks) ? blocks.map(normalizeContentBlockFromApi) : blocks),
    z.array(articleContentBlockSchema).min(1)
  ),
});

export const articleCategorySchema = z.object({
  name: z.string().min(1),
});

export const articleSummarySchema = z.object({
  id: z.coerce.string(),
  title: z.string(),
  subtitle: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  readingMinutes: z.coerce.number().int().nullable().optional(),
  publishedAt: isoDateTimeSchema.nullable().optional(),
  isVisible: z.coerce.boolean().nullable().optional(),
});

export const articleSchema = articleSummarySchema.extend({
  content: articleContentSchema,
  linkedArticles: z.preprocess(
    (value) => (Array.isArray(value) ? value.map(String) : []),
    z.array(z.string())
  ).optional().default([]),
  suggestedProducts: z.preprocess(
    (value) => (Array.isArray(value) ? value.map(String) : []),
    z.array(z.string())
  ).optional().default([]),
});

export const articleSavePayloadSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1),
  subtitle: z.string().optional(),
  image: z.string().min(1),
  content: articleContentSchema,
  category: z.string().min(1),
  linkedArticles: z.array(z.string()).max(3).optional(),
  suggestedProducts: z.array(z.string()).optional(),
  readingMinutes: z.number().int().min(1).optional(),
  publishedAt: z.string().optional(),
  isVisible: z.boolean(),
});

export const storageUploadResultSchema = z.object({
  objectKey: z.string(),
  publicUrl: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.coerce.number(),
  uploadedAt: isoDateTimeSchema,
});

export const storageObjectSummarySchema = z.object({
  objectKey: z.string(),
  fileName: z.string(),
  contentType: z.string().nullable().optional(),
  sizeBytes: z.coerce.number(),
  lastModified: isoDateTimeSchema.nullable().optional(),
  publicUrl: z.string().nullable().optional(),
});

export const bannerSchema = z.object({
  objectKey: z.string().min(1),
  publicUrl: z.string().min(1),
  posizione: z.coerce.number().int().min(1),
  deviceMode: z.enum(["desktop", "mobile"]),
  isActive: z.coerce.number().int().min(0).max(1),
});

export const bannerUpdatePayloadSchema = z.object({
  objectKey: z.string().min(1),
  posizione: z.number().int().min(1),
  deviceMode: z.enum(["desktop", "mobile"]),
  isActive: z.number().int().min(0).max(1),
});

const normalizeArticlePayload = (data) => {
  if (!data || typeof data !== "object") {
    return data;
  }
  return {
    ...data,
    content: data.content
      ? articleContentSchema.parse({
          version: data.content.version ?? 1,
          blocks: data.content.blocks ?? [],
        })
      : data.content,
  };
};

export const parseArticleCategories = (data) => z.array(articleCategorySchema).parse(Array.isArray(data) ? data : []);
export const parseArticleSummaries = (data) => z.array(articleSummarySchema).parse(Array.isArray(data) ? data : []);
export const parseArticle = (data) => articleSchema.parse(normalizeArticlePayload(data));
export const parseStorageUploadResult = (data) => storageUploadResultSchema.parse(data);
export const parseStorageObjectSummaries = (data) =>
  z.array(storageObjectSummarySchema).parse(Array.isArray(data) ? data : []);
export const parseBanners = (data) => z.array(bannerSchema).parse(Array.isArray(data) ? data : []);
export const parseBanner = (data) => bannerSchema.parse(data);

export const formatSchemaParseError = (error, context) => {
  if (!(error instanceof z.ZodError)) {
    return error?.message || `Errore imprevisto durante ${context}.`;
  }
  const details = error.issues.map((issue) => `${issue.path.join(".") || "payload"}: ${issue.message}`).join("; ");
  return `Risposta non valida (${context})${details ? `: ${details}` : "."}`;
};
